import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { LiveEvent } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
	Button,
	Stack,
	Text
} from "@mantine/core";
import { IconAlertCircle, IconMicrophone2 } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useState } from "react";

export const projectStatuses = ["not submitted", "submitted for review", "approved", "up next in queue", "performed"] as const;
export type ProjectStatus = (typeof projectStatuses)[number];

interface SendToLiveEventMusiciansFormProps {
	onClose: () => void;
}

const SendToLiveEventMusiciansForm: React.FC<SendToLiveEventMusiciansFormProps> = ({ onClose }) => {
	const auth = useContext(AuthContext);
	if (auth === undefined) {
		throw new Error("Modal must be a child of AuthContext.");
	}
	const token = auth.token;
	const user = auth.user;
	const project = auth.project;
	if (token === null || user === null || project === null) {
		onClose();
		return;
	}

	const [liveEvent, setLiveEvent] = useState<LiveEvent | null>(auth.activeParticipatingLiveEvent ?? auth.activeAdministratingLiveEvent ?? auth.activePlayingLiveEvent);

	if (liveEvent === null) {
		Utils.displayNotification(
			"You must be logged in with an active live event open in order to send to live event musicians.",
			undefined,
			"blue",
			<IconAlertCircle size={16} />,
		);
		onClose();
		return;
	}

	const [projectStatus, setProjectStatus] = useState<ProjectStatus>("not submitted");

	useEffect(() => {
		setLiveEvent(auth.activeParticipatingLiveEvent ?? auth.activeAdministratingLiveEvent ?? auth.activePlayingLiveEvent);
	}, [auth.activeParticipatingLiveEvent, auth.activeAdministratingLiveEvent, auth.activePlayingLiveEvent]);

	useEffect(() => {
		const performanceQueueIndex = liveEvent.getProjectPerformanceQueue()?.findIndex((p) => p.getId() === project.getId()) ?? -1;
		const performanceQueuePosition = liveEvent.getProjectPerformanceQueuePosition() ?? 0;
		if (liveEvent.getProjectsReadyForReview()?.find((p) => p.getId() === project.getId()) != null) {
			setProjectStatus("submitted for review");
		} else if (performanceQueueIndex != -1 && performanceQueueIndex > performanceQueuePosition) {
			setProjectStatus("approved");
		} else if (performanceQueueIndex != -1 && performanceQueueIndex === performanceQueuePosition) {
			setProjectStatus("up next in queue");
		} else if (performanceQueueIndex != -1 && performanceQueueIndex < performanceQueuePosition) {
			setProjectStatus("performed");
		} else {
			setProjectStatus("not submitted");
		}
	}, [liveEvent]);

	const handleSubmit = useCallback(async () => {
		if (project === null || user === null || user.roles.includes("Guest")) {
			Utils.displayNotification(
				"You must be logged in with an active project and active live event open in order to send to live event musicians.",
				undefined,
				"blue",
				<IconAlertCircle size={16} />,
			);
			return;
		}

		if (project.isDirty() && !project.isLocked() && project.userCanEdit(user)) {
			await auth.saveProject();
		}

		const projectId = project.getId();

		const notificationId = Utils.displayNotification(
			"Attempting submission...",
			"This could take a few seconds.",
			"blue",
			"loading"
		);

		try {
			await ConductionAPI.submitProjectForLiveEvent(
				liveEvent.id,
				projectId,
				token
			);

			Utils.updateNotification(
				notificationId,
				"Success!",
				"This project has been sent in for review!",
				"green",
				<IconMicrophone2 size={16} />
			);

			auth.lockProject();
			auth.updateLiveEvents();
		} catch {
			Utils.updateNotification(
				notificationId,
				"Submission Error",
				"Something went wrong while submitting this project. We are sorry. Please try again later.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [project, liveEvent, token]);

	return (
		<ConductionModal
			onClose={onClose}
			title="Send to Musicians"
			size="lg"
		>
			{projectStatus === "submitted for review" ? (
				<Stack
					align="center"
					justify="center"
				>
					<Text size="md">
						This project is submitted for review! It will be reviewed by the event admins before being sent to musicians.
					</Text>

					<Text>
						This project is locked right now, but you will be able to edit this project more if it is not approved.
					</Text>
				</Stack>
			) : projectStatus === "approved" ? (
				<Stack
					align="center"
					justify="center"
				>
					<Text size="md">
						Your project has been approved and is in the queue to be performed!
					</Text>

					<Text>
						You will not be able to edit this project until after the event.
					</Text>
				</Stack>
			) : projectStatus === "up next in queue" ? (
				<Stack
					align="center"
					justify="center"
				>
					<Text size="md">
						Your project is up next in the queue to be performed!
					</Text>

					<Text>
						You will not be able to edit this project until after the event.
					</Text>
				</Stack>
			) : projectStatus === "performed" ? (
				<Stack
					align="center"
					justify="center"
				>
					<Text size="md">
						Bravo! Your project has been performed!
					</Text>

					<Text>
						You will not be able to edit this project until after the event.
					</Text>

					<Button
						onClick={() => auth.openProject()}
					>
						<Text>
							Click here to start a new project!
						</Text>
					</Button>
				</Stack>
			) : (
				<Stack
					align="center"
					justify="center"
				>
					<Text size="md">Ready to see your creation come to life with real musicians?</Text>

					<Text>
						Submitting this project to be recorded will <strong>freeze the project</strong> until after this event.
					</Text>

					<Button
						onClick={handleSubmit}
						// Disable during tour
						disabled={auth.beginnerTourActive}
					>
						<Text>
							Lock project and submit to musicians!
						</Text>
					</Button>
				</Stack>
			)}
		</ConductionModal>
	);
};

export default SendToLiveEventMusiciansForm;